import React, { useState, useEffect } from "react";
import { Plus, Search, Edit, Trash2, Save, X, Upload } from "lucide-react";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Header from "../Components/Header";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase/config";
import toast from "react-hot-toast";

const AITemplatesView = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const [templates, setTemplates] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    jsonFile: null,
    tags: [],
    featured: false,
  });

  const [newTag, setNewTag] = useState("");

  // React Quill configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link', 'blockquote', 'code-block'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'color', 'background',
    'link', 'blockquote', 'code-block'
  ];

  // Fetch templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "agentTemplates"));
        const templatesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setTemplates(templatesData);
      } catch (error) {
        console.error("Error fetching templates:", error);
        toast.error("Error fetching templates");
      }
    };
    fetchTemplates();
  }, []);

  // Filter templates based on search
  const filteredTemplates = templates.filter(template =>
    template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleDescriptionChange = (content) => {
    setFormData((prev) => ({
      ...prev,
      description: content,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "application/json") {
      setFormData((prev) => ({
        ...prev,
        jsonFile: file,
      }));
    } else {
      toast.error("Please select a valid JSON file");
    }
  };

  const addTag = (tag) => {
    if (tag && !formData.tags.includes(tag)) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tag],
      }));
    }
    setNewTag("");
  };

  const removeTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.description) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsLoading(true);

    try {
      let downloadURL = selectedTemplate?.jsonUrl;

      // Upload new JSON file if provided
      if (formData.jsonFile) {
        const fileRef = ref(
          storage,
          `agentTemplates/${Date.now()}_${formData.jsonFile.name}`
        );
        await uploadBytes(fileRef, formData.jsonFile);
        downloadURL = await getDownloadURL(fileRef);
      }

      if (selectedTemplate) {
        // Update existing template
        const templateRef = doc(db, "agentTemplates", selectedTemplate.id);
        await updateDoc(templateRef, {
          title: formData.title,
          description: formData.description,
          tags: formData.tags,
          featured: formData.featured,
          jsonUrl: downloadURL,
          updatedAt: serverTimestamp(),
        });
        toast.success("Template updated successfully!");
      } else {
        // Add new template
        await addDoc(collection(db, "agentTemplates"), {
          title: formData.title,
          description: formData.description,
          tags: formData.tags,
          featured: formData.featured,
          jsonUrl: downloadURL,
          createdAt: serverTimestamp(),
        });
        toast.success("Template added successfully!");
      }

      // Refresh templates
      const querySnapshot = await getDocs(collection(db, "agentTemplates"));
      setTemplates(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      closeModal();
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Error saving template. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setFormData({
      title: template.title,
      description: template.description,
      jsonFile: null,
      tags: template.tags,
      featured: template.featured,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this template?")) {
      try {
        await deleteDoc(doc(db, "agentTemplates", id));
        setTemplates(templates.filter(template => template.id !== id));
        toast.success("Template deleted successfully!");
      } catch (error) {
        console.error("Error deleting template:", error);
        toast.error("Error deleting template");
      }
    }
  };

  const openModal = () => {
    setIsModalOpen(true);
    setSelectedTemplate(null);
    setFormData({
      title: "",
      description: "",
      jsonFile: null,
      tags: [],
      featured: false,
    });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTemplate(null);
    setFormData({
      title: "",
      description: "",
      jsonFile: null,
      tags: [],
      featured: false,
    });
  };

  return (
    <div className="overflow-hidden">
      <Header
        PageName={"AI Templates"}
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
      />

      <div className="bg-[#0a1344] overflow-y-auto max-h-[96%] p-6 scrollbar-thin rounded-lg border border-gray-600">
        <div className=" mx-auto">
          <div className="flex justify-between items-center mb-6">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-[#1B2255] text-white border border-gray-500 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={openModal}
              className="flex items-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Plus className="h-5 w-5" />
              <span>Add Template</span>
            </button>
          </div>

          <div className="bg-[#0c0c42] rounded-lg border border-gray-500 shadow-xl overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-[#1B2255] border-b border-gray-500">
                <tr>
                  <th className="px-6 py-3 text-gray-300 font-semibold">Title</th>
                  <th className="px-6 py-3 text-gray-300 font-semibold">Tags</th>
                  <th className="px-6 py-3 text-gray-300 font-semibold">Featured</th>
                  <th className="px-6 py-3 text-gray-300 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTemplates.map((template) => (
                  <tr key={template.id} className="border-b border-gray-500 hover:bg-[#1B2255]">
                    <td className="px-6 py-4 text-white">{template.title}</td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-2">
                        {template.tags.map((tag) => (
                          <span key={tag} className="px-2 py-1 bg-pink-100 text-pink-800 text-xs rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-white">
                      {template.featured ? "Yes" : "No"}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex space-x-4">
                        <button
                          onClick={() => handleEdit(template)}
                          className="text-blue-400 hover:text-blue-300"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(template.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#0c0c42] rounded-lg border border-gray-500 w-full max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">
                {selectedTemplate ? "Update Template" : "Add New Template"}
              </h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-300">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Title */}
              <div>
                <label className="block text-start text-sm font-semibold text-gray-300 mb-2">
                  Template Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., Ultimate LinkedIn Automation"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-[#1B2255] text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-start text-sm font-semibold text-gray-300 mb-2">
                  Description *
                </label>
                <div className="quill-dark-theme">
                  <ReactQuill
                    theme="snow"
                    value={formData.description}
                    onChange={handleDescriptionChange}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Describe what this AI agent does..."
                    style={{
                      backgroundColor: '#1B2255',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db'
                    }}
                  />
                </div>
              </div>

              {/* JSON File Upload */}
              <div>
                <label className="block text-start text-sm font-semibold text-gray-300 mb-2">
                  JSON Template File {selectedTemplate ? "(Optional)" : "*"}
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-400 transition-colors">
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleFileChange}
                    className="hidden"
                    id="jsonFile"
                  />
                  <label htmlFor="jsonFile" className="cursor-pointer">
                    <Upload className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                    <p className="text-gray-600">
                      {formData.jsonFile
                        ? formData.jsonFile.name
                        : selectedTemplate
                        ? "Upload new JSON file (optional)"
                        : "Click to upload JSON file"}
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      Only JSON files are accepted
                    </p>
                  </label>
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-start text-sm font-semibold text-gray-300 mb-2">
                  Tools & Tags
                </label>
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Enter tag/tool name"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-[#1B2255] text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    onKeyDown={(e) =>
                      e.key === "Enter" && (e.preventDefault(), addTag(newTag))
                    }
                  />
                  <button
                    type="button"
                    onClick={() => addTag(newTag)}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                {formData.tags.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Selected tags:</p>
                    <div className="flex flex-wrap gap-2">
                      {formData.tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center px-3 py-1 bg-pink-100 text-pink-800 text-sm rounded-full"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="ml-2 text-pink-600 hover:text-pink-800"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Featured */}
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="featured"
                  name="featured"
                  checked={formData.featured}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="featured"
                  className="text-sm font-semibold text-gray-300 cursor-pointer"
                >
                  Mark as Featured Template
                </label>
              </div>

              {/* Submit */}
              <div className="pt-6 flex space-x-4">
                <button
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="flex-1 bg-[#1f53dc] py-3 px-6 text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>{selectedTemplate ? "Updating..." : "Adding..."}</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      <span>{selectedTemplate ? "Update Template" : "Add Template"}</span>
                    </>
                  )}
                </button>
                <button
                  onClick={closeModal}
                  className="flex-1 bg-gray-600 py-3 px-6 text-white rounded-lg font-semibold hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Styles for React Quill Dark Theme */}
      <style jsx global>{`
        .quill-dark-theme .ql-toolbar {
          background-color: #fff;
          border-top: 1px solid #d1d5db;
          border-left: 1px solid #d1d5db;
          border-right: 1px solid #d1d5db;
          border-bottom: none;
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
        }
        
        .quill-dark-theme .ql-toolbar .ql-stroke {
          stroke: #000;
        }
        
        .quill-dark-theme .ql-toolbar .ql-fill,
        .quill-dark-theme .ql-toolbar .ql-stroke.ql-fill {
          fill: #000;
        }
        
        .quill-dark-theme .ql-toolbar .ql-picker-label {
          color: #000;
        }
        
        .quill-dark-theme .ql-container {
          background-color: #1B2255;
          border-bottom: 1px solid #d1d5db;
          border-left: 1px solid #d1d5db;
          border-right: 1px solid #d1d5db;
          border-top: none;
          border-bottom-left-radius: 8px;
          border-bottom-right-radius: 8px;
          min-height: 120px;
        }
        
        .quill-dark-theme .ql-editor {
          color: #ffffff;
          min-height: 100px;
        }
        
        .quill-dark-theme .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: italic;
        }
        
        .quill-dark-theme .ql-toolbar button:hover {
          background-color: #374151;
        }
        
        .quill-dark-theme .ql-toolbar button.ql-active {
          background-color: #7c3aed;
        }
        
        .quill-dark-theme .ql-picker-options {
          background-color: #1B2255;
          border: 1px solid #d1d5db;
        }
        
        .quill-dark-theme .ql-picker-item {
          color: #ffffff;
        }
        
        .quill-dark-theme .ql-picker-item:hover {
          background-color: #374151;
        }
      `}</style>
    </div>
  );
};

export default AITemplatesView;